/* ===========================================================================
   cm-cart-status.js
   SIDE-CART BANNER + PLUS-BUTTON LOCKS (OL STATE-DRIVEN)
   ---------------------------------------------------------------------------
   ▶ Injects OL-style banner into cart drawer (side cart)
   ▶ Uses the same OL variables as main banner:
       --ol-banner-ok-bg / --ol-banner-warn-bg / --ol-banner-err-bg
   ▶ Uses the same allocation-state logic:
       is-ok / is-warn / is-err
   ▶ Replaces the subtotal block in the drawer
   ▶ Disables + buttons in #ol-feed when allocation is reached
   ▶ Requires: cm-allocation-core.js (window.OL) loaded BEFORE this script
   =========================================================================== */

/* ===========================================================================
   DRAWER BANNER
   =========================================================================== */
(function(){
  'use strict';

  /* -------------------------------------------------------------------------
     Inject CSS for the drawer banner
     ------------------------------------------------------------------------- */
  (function injectCSS(){
    if (document.getElementById('ol-drawer-banner-css')) return;

    const css = `
      .ol-alloc-banner {
        background: var(--ol-banner-warn-bg);
        color: var(--ol-banner-warn-txt);
        width: 100%;
        min-height: 3rem;
        padding: 2rem 1.5rem;
        margin: .5rem 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
        font-size: 1.05em;
        box-shadow: 0 2px 4px rgba(0,0,0,.03);
        text-align: center;
      }

      .ol-alloc-banner.is-ok {
        background: var(--ol-banner-ok-bg);
        color: var(--ol-banner-ok-txt);
      }

      .ol-alloc-banner.is-warn {
        background: var(--ol-banner-warn-bg);
        color: var(--ol-banner-warn-txt);
      }

      .ol-alloc-banner.is-err {
        background: var(--ol-banner-err-bg);
        color: var(--ol-banner-err-txt);
      }

      .ol-alloc-banner strong {
        font-weight: 600;
      }
    `;

    const s = document.createElement('style');
    s.id = 'ol-drawer-banner-css';
    s.appendChild(document.createTextNode(css));
    document.head.appendChild(s);
  })();


  /* -------------------------------------------------------------------------
     Drawer footer lookup. Different Shopify themes structure drawers
     differently, so we use a selector list to support all variants.
     ------------------------------------------------------------------------- */
  const DRAWER_FOOTER_SEL =
    '.drawer__footer .cart-drawer__footer, ' +
    '#CartDrawer .cart-drawer__footer, ' +
    'cart-drawer .cart-drawer__footer, ' +
    '.CartDrawer .cart-drawer__footer';

  function findDrawerFooters(){
    return Array.from(
      document.querySelectorAll(DRAWER_FOOTER_SEL)
    ).filter(el =>
      el.closest('#CartDrawer, cart-drawer, .drawer, [role="dialog"], .cart-drawer')
    );
  }


  /* -------------------------------------------------------------------------
     resolveState(st)
     Match the emoji/main-banner "face" logic:
        "ok", "warn", "err"
     ------------------------------------------------------------------------- */
  function resolveState(st){
    if (!st || !st.allow) return 'err';

    const total = st.totals.g;
    const limit = st.allow.g;

    if (limit === 0) return 'err';
    if (st.flags.overS || st.flags.overM) return 'err';
    if (limit == null) return 'warn';
    if (total > limit) return 'err';
    if (total < limit) return 'warn';
    return 'ok';
  }


  /* -------------------------------------------------------------------------
     buildDrawerMsg(st)
     Drawer-friendly version of the main OL message:

     - No greeting ("Hi Carl, your code...")
     - Same overage logic / wording:
         Over in: moisturizers 5/4 (+1) • eye colours 3/2 (+1)
       OR multiline if ≥3 entries
     - Same Cart line:
         Cart: 9/10 (1 remaining).
         Cart: 11/10 (Remove 1).
     ------------------------------------------------------------------------- */
  function buildDrawerMsg(st){
    if (!st || !st.allow) return 'Allocation unavailable.';

    const allow = st.allow;
    const totals = st.totals || { s:0, m:0, g:0 };
    const total = totals.g;
    const limit = allow.g;
    const s = allow.s;
    const m = allow.m;
    const remaining = (limit == null ? null : (limit - total));

    // Special case: already-ordered profile
    const profileTag = (st.profileTag || '').toUpperCase();
    if (profileTag === 'FT-ORDERED') {
      const when = (window.OL_ORDER_DATE || '').trim();
      const campaign = (window.OL_CAMPAIGN || '').trim();
      const whenPart = when ? ` on <strong>${when}</strong>` : '';
      const campaignPart = campaign ? ` for <strong>${campaign}</strong>` : ' for this quarter';
      return `You’ve already placed your order${campaignPart}${whenPart}.`;
    }

    if (limit === 0) {
      return `You’re not yet eligible for an allocation.`;
    }

    const hasOver =
      st.flags.overS ||
      st.flags.overM ||
      (limit != null && total > limit) ||
      (st.overages && st.overages.length > 0) ||
      (st.sectionOverages && st.sectionOverages.length > 0);

    function buildCartLine(){
      if (limit == null) {
        return `Cart: ${total}.`;
      }

      const totalOver = (limit != null && total > limit) ? (total - limit) : 0;
      if (totalOver > 0) {
        return `Cart: ${total}/${limit} (Remove ${totalOver}).`;
      }
      return `Cart: ${total}/${limit} (${Math.max(0, remaining)} remaining).`;
    }

    if (hasOver){
      const subBits = (st.overages || []).map(o =>
        `${o.name} ${o.count}/${o.max} (+${o.over})`
      );
      const secBits = (st.sectionOverages || []).map(o =>
        `${o.section} ${o.total}/${o.allow} (+${o.over})`
      );
      const allBits = secBits.concat(subBits);

      let overLine = '';

      if (allBits.length === 0) {
        overLine = '';
      } else if (allBits.length < 3) {
        // One line, bullet-style separators
        overLine = `Over in: ${allBits.join(' • ')}`;
      } else {
        // Multiline for readability
        overLine = 'Over in:<br>- ' + allBits.join('<br>- ');
      }

      const cartLine = buildCartLine();
      if (overLine) {
        return `${overLine}<br>${cartLine}`;
      }
      return cartLine;
    }

    // Non-over states
    if (limit == null) {
      return `No fixed total cap. Section caps: ${s} Skincare, ${m} Makeup.<br>Cart: ${total}.`;
    }

    if (total < limit) {
      return `You can order up to ${limit} products<br> (${s} Skincare, ${m} Makeup).<br>Cart: ${total}/${limit} (${Math.max(0, remaining)} remaining).`;
    }

    // total === limit
    return `You’ve reached your allocation.<br>Cart: ${total}/${limit} (0 remaining).`;
  }


  /* -------------------------------------------------------------------------
     ensureBanner(footer)
     Ensures the drawer banner exists, and hides the default subtotal block.
     ------------------------------------------------------------------------- */
  function ensureBanner(footer){
    if (!footer) return null;

    const totals = footer.querySelector('.totals');
    if (totals && !totals.hasAttribute('data-ol-hidden')) {
      totals.setAttribute('data-ol-hidden','');
      totals.style.display = 'none';
    }

    footer.querySelectorAll('small.tax-note').forEach(n => {
      if (!n.hasAttribute('data-ol-hidden')) {
        n.setAttribute('data-ol-hidden','');
        n.style.display = 'none';
      }
    });

    let banner = footer.querySelector('[data-ol-drawer-banner]');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'ol-alloc-banner';
      banner.setAttribute('data-ol-drawer-banner','');
      banner.setAttribute('aria-live','polite');

      if (totals && totals.parentNode) {
        totals.parentNode.insertBefore(banner, totals);
      } else {
        footer.insertAdjacentElement('afterbegin', banner);
      }
    }
    return banner;
  }


  /* -------------------------------------------------------------------------
     paint(st)
     Updates banner message + state class.
     ------------------------------------------------------------------------- */
let lastBannerHTML = "";   // tracks previous banner output
let lastBannerState = "";  // tracks previous warning/ok/error state
let paintTimer = null;     // debounce updates

function paint(st){
  clearTimeout(paintTimer);

  paintTimer = setTimeout(() => {

    findDrawerFooters().forEach(footer => {
      const banner = ensureBanner(footer);
      if (!banner) return;

      const newHTML  = buildDrawerMsg(st);
      const newState = resolveState(st);

      const htmlChanged  = (newHTML !== lastBannerHTML);
      const stateChanged = (newState !== lastBannerState);

      // Only animate if something actually changed
      if (htmlChanged || stateChanged) {

        banner.classList.add("is-updating");

        // Apply updates after fade-out starts
        setTimeout(() => {
          banner.innerHTML = newHTML;

          banner.classList.remove("is-ok", "is-warn", "is-err");
          banner.classList.add("is-" + newState);

          banner.classList.remove("is-updating");

          lastBannerHTML = newHTML;
          lastBannerState = newState;

        }, 120); // small delay for fade-out
      }
    });

  }, 50); // debounce Shopify rapid redraws
}



  /* -------------------------------------------------------------------------
     OL State Listener
     ------------------------------------------------------------------------- */
  document.addEventListener('ol:state', e => {
    window.__olLastState = e.detail;
    paint(e.detail);
  });


  /* -------------------------------------------------------------------------
     Observe DOM for when drawer re-renders
     ------------------------------------------------------------------------- */
  const mo = new MutationObserver(() =>
    paint(window.__olLastState || null)
  );
  try { mo.observe(document.body, { childList:true, subtree:true }); } catch(_) {}


  /* -------------------------------------------------------------------------
     Initial seed + refresh
     ------------------------------------------------------------------------- */
  function seed(){
    if (window.OL && typeof OL.refresh === 'function') {
      OL.refresh()
        .then(st => { window.__olLastState = st; paint(st); })
        .catch(() => { paint(window.__olLastState || null); });
    } else {
      paint(window.__olLastState || null);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', seed);
  } else {
    seed();
  }


  /* -------------------------------------------------------------------------
     Refresh when drawer is opened (ensures fresh state)
     ------------------------------------------------------------------------- */
  document.addEventListener('click', e => {
    const opener = e.target.closest(
      '#cart-icon-bubble, .header__icon--cart, ' +
      'button[aria-controls="CartDrawer"], [data-toggle="cart-drawer"], [data-action="open-drawer"]'
    );
    if (!opener) return;

    let n = 0;
    const id = setInterval(() => {
      seed();
      if (++n > 10) clearInterval(id);
    }, 200);
  });

})();


/* ============================================================
   LIVE LOCKING — disables + buttons BEFORE clicking
   ============================================================ */
function updateDrawerPlusButtons(st) {
  if (!st || !st.cart) return;

  const maxMap    = st.max;
  const counters  = st.counters;
  const totals    = st.totals;
  const allow     = st.allow;

  document.querySelectorAll(".cart-item").forEach(itemEl => {
    const btn = itemEl.querySelector(".quantity__button[name='plus']");
    const qtyInput = itemEl.querySelector(".quantity__input");
    if (!btn || !qtyInput) return;

    const lineIndex = parseInt(qtyInput.dataset.index, 10);
    const item = st.cart.items[lineIndex - 1];
    if (!item) return;

    const key = OL.detectGroup(item);
    if (!key) return;

    const used = counters[key] || 0;
    const max  = maxMap[key] || 0;

    const isSkin = OL.constants.KEYS.skincare.includes(key);
    const sectionUsed  = isSkin ? totals.s : totals.m;
    const sectionAllow = isSkin ? allow.s : allow.m;

    const willHitSub     = (max > 0 && used >= max);
    const willHitSection = (sectionAllow >= 0 && sectionUsed >= sectionAllow);
    const willHitGlobal  = (allow.g >= 0 && totals.g >= allow.g);

    if (willHitSub || willHitSection || willHitGlobal) {
      btn.classList.add("cm-cart-plus-disabled");
      btn.disabled = true;
    } else {
      btn.classList.remove("cm-cart-plus-disabled");
      btn.disabled = false;
    }
  });
}

// Listen for OL updates
document.addEventListener("ol:state", e => updateDrawerPlusButtons(e.detail));




/* ========================================================================
   FINAL STRONG BLOCKER — synchronous, zero overs
   ======================================================================== */
(function(){
  if (!window.OL) return;

  let st = window.__olLastState || null;
  document.addEventListener("ol:state", e => st = e.detail);

  document.addEventListener("click", function(e){
    const btn = e.target.closest(".cart-item .quantity__button[name='plus']");
    if (!btn) return;
    if (!st || !st.cart) return;

    const itemEl = btn.closest(".cart-item");
    const qtyInput = itemEl.querySelector(".quantity__input");
    if (!qtyInput) return;

    const lineIndex = parseInt(qtyInput.dataset.index, 10);
    const item = st.cart.items[lineIndex - 1];
    if (!item) return;

    const key = OL.detectGroup(item);
    if (!key) return;

    const used = st.counters[key] || 0;
    const max  = st.max[key] || 0;

    const isSkin = OL.constants.KEYS.skincare.includes(key);
    const sectionUsed  = isSkin ? st.totals.s : st.totals.m;
    const sectionAllow = isSkin ? st.allow.s  : st.allow.m;

    const globalUsed  = st.totals.g;
    const globalAllow = st.allow.g;

    const willHitSub     = (max > 0 && used >= max);
    const willHitSection = (sectionAllow >= 0 && sectionUsed >= sectionAllow);
    const willHitGlobal  = (globalAllow >= 0 && globalUsed >= globalAllow);

    if (willHitSub || willHitSection || willHitGlobal) {
      e.preventDefault();
      e.stopImmediatePropagation();
      btn.classList.add("cm-cart-plus-disabled");
      console.warn("OL BLOCK — SYNCHRONOUS:", {
        key, used, max, sectionUsed, sectionAllow, globalUsed, globalAllow
      });
      return false;
    }

  }, true); // capture — MUST REMAIN TRUE
})();




/* ===========================================================================
   PLUS BUTTON LOCKS IN #ol-feed (state-machine driven)
   =========================================================================== */
(function(){
  "use strict";

  if (!window.OL || !OL.constants || !OL.constants.KEYS || !OL.constants.MAX) {
    // OL core not present → nothing to do
    return;
  }

  // Precompute key → section lookup from OL constants
  const SKINCARE_KEYS = new Set(OL.constants.KEYS.skincare || []);
  const MAKEUP_KEYS  = new Set(OL.constants.KEYS.makeup  || []);

  let lastState = null;

  function shouldLockPlus(st, key){
    if (!st || !st.allow) return false;

    const allow = st.allow;
    const totals = st.totals || { s:0, m:0, g:0 };
    const counters = st.counters || {};
    const maxMap = st.max || OL.constants.MAX || {};

    let lock = false;

    // Section caps (Skincare / Makeup)
    if (SKINCARE_KEYS.has(key)) {
      const sAllow = allow.s;
      if (typeof sAllow === 'number' && sAllow >= 0 && totals.s >= sAllow) {
        lock = true;
      }
    }
    if (!lock && MAKEUP_KEYS.has(key)) {
      const mAllow = allow.m;
      if (typeof mAllow === 'number' && mAllow >= 0 && totals.m >= mAllow) {
        lock = true;
      }
    }

    // Subcategory cap (per group key)
    if (!lock) {
      const used = counters[key] || 0;
      const max = maxMap[key] || OL.constants.MAX[key] || 0;
      if (max > 0 && used >= max) {
        lock = true;
      }
    }

    // Global cap (total products)
    if (!lock) {
      const gAllow = allow.g;
      if (typeof gAllow === 'number' && gAllow >= 0 && totals.g >= gAllow) {
        lock = true;
      }
    }

    return lock;
  }

  function updatePlusButtons(st){
    if (!st || !st.allow) return;

    const rows = document.querySelectorAll("#ol-feed .ol-subitem--injected");
    if (!rows.length) return;

    rows.forEach(row => {
      const plus  = row.querySelector(".ol-plus");
      const input = row.querySelector(".ol-qty input");
      if (!plus || !input) return;

      const key =
        row.getAttribute("data-ol-group-key") ||
        row.getAttribute("data-key") ||
        "";

      if (!key) return;

      const lock = shouldLockPlus(st, key);

      if (lock) {
        plus.classList.add("ol-plus-disabled");
        // Keep button focusable for accessibility; just use class to visually/behaviourally lock
        plus.removeAttribute("disabled");
      } else {
        plus.classList.remove("ol-plus-disabled");
      }
    });
  }

  // React to OL allocation state changes
  document.addEventListener("ol:state", function(e){
    lastState = e.detail;
    updatePlusButtons(lastState);
  });

  // If OL state was emitted before this script loaded
  if (window.__olLastState) {
    lastState = window.__olLastState;
    updatePlusButtons(lastState);
  }

  // When the allocation table re-renders (e.g. theme dynamic updates), re-apply locks
  const feed = document.getElementById("ol-feed");
  if (feed) {
    const mo = new MutationObserver(() => {
      if (lastState || window.__olLastState) {
        updatePlusButtons(lastState || window.__olLastState);
      }
    });
    try {
      mo.observe(feed, { childList: true, subtree: true });
    } catch (_) {}
  }

})();


